import { type ButtonHTMLAttributes, type CSSProperties, type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CircleAlert } from 'lucide-react'
import { failedMark, toneClass, type Tone } from '@/lib/colour'
import { CloseGlyph } from '@/components/CloseGlyph'
import { useBrowserOnline } from '@/hooks/useBrowserOnline'

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ')

/** A chip's look, shared by `<Button variant="chip">` and `CHIP_CLASS` (a link that is a card action). */
const CHIP_LOOK =
  'rounded-full bg-raised border border-line text-fg-2 hover:border-line-strong min-h-8 px-3 text-xs whitespace-nowrap'

type Variant = 'primary' | 'ghost' | 'danger' | 'chip' | 'chipOn' | 'quiet'

/**
 * Button hierarchy: primary (one per view), ghost (bordered), quiet (text-only),
 * danger, and chips (round, for filters/triage). Touch targets ≥ 36px, 44px when `big`.
 */
export const Button = ({
  variant = 'ghost',
  className,
  big,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; big?: boolean }) => (
  <button
    type="button"
    className={cx(
      'inline-flex items-center justify-center gap-1.5 font-medium select-none transition-[background-color,border-color,color,transform] duration-100 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none',
      big ? 'min-h-11 px-4 text-[15px]' : 'min-h-9 px-3 text-sm',
      variant === 'primary' &&
        'rounded-[var(--radius-control)] bg-accent text-on-accent font-semibold hover:brightness-110',
      variant === 'ghost' &&
        'rounded-[var(--radius-control)] bg-raised border border-line text-fg hover:border-line-strong hover:bg-panel',
      variant === 'quiet' && 'rounded-[var(--radius-control)] text-fg-2 hover:bg-raised',
      variant === 'danger' &&
        'rounded-[var(--radius-control)] bg-danger/10 border border-danger/35 text-danger hover:bg-danger/15 hover:border-danger/60',
      variant === 'chip' && CHIP_LOOK,
      variant === 'chipOn' && 'rounded-full bg-accent text-on-accent min-h-8 px-3 text-xs whitespace-nowrap',
      className,
    )}
    {...rest}
  />
)

/**
 * A secondary action on a card, as a link or anchor: the same round chip as
 * `<Button variant="chip">`, so a card's actions read as one set (Review first,
 * Open the agent, On Forgejo).
 */
export const CHIP_CLASS = `inline-flex items-center justify-center gap-1.5 font-medium select-none ${CHIP_LOOK}`

export const IconButton = ({
  label,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={cx(
      'inline-flex items-center justify-center rounded-[var(--radius-control)] min-h-10 min-w-10 text-muted hover:bg-raised hover:text-fg transition-colors',
      className,
    )}
    {...rest}
  />
)

/** Small metadata pill. Tone from the colour system, or an inline style for label/workspace colours. */
export const Tag = ({
  children,
  tone = 'muted',
  style,
  title,
}: {
  children: ReactNode
  tone?: Tone
  style?: CSSProperties
  title?: string
}) => (
  <span
    className={cx(
      'inline-flex items-center rounded-md px-1.5 h-5 text-[11px] leading-none whitespace-nowrap font-medium',
      !style && toneClass[tone],
    )}
    style={style}
    title={title}
  >
    {children}
  </span>
)

/**
 * Nothing to show, said plainly: an icon when one helps (a lucide icon, as the
 * rest of the app uses; never a lone dot), the words, and a next step.
 */
export const Empty = ({
  children,
  icon,
  action,
}: {
  children: ReactNode
  icon?: ReactNode
  action?: ReactNode
}) => (
  <div className="text-center py-10 fade-in">
    {icon && <div className="text-3xl mb-2 opacity-60 flex justify-center text-muted">{icon}</div>}
    <p className="text-muted text-sm max-w-xs mx-auto leading-6">{children}</p>
    {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
  </div>
)

/**
 * A read from the server that failed, in one shape on every online-only page:
 * a sentence saying what could not be loaded and what to do, the server's
 * reason underneath, and Try again where asking again can help. Offline says
 * that instead (the reason would only be the network's). `what` is a noun
 * phrase ("the builds", "your listening"). `compact` is the form for a
 * widget's own frame: one line, the reason kept for a hover.
 */
export const Failed = ({
  what,
  reason,
  onRetry,
  hint,
  compact,
  className,
}: {
  what: string
  reason?: string | null
  onRetry?: () => void
  /** what to do, when it is not "try again in a moment" */
  hint?: string
  compact?: boolean
  className?: string
}) => {
  const online = useBrowserOnline()
  const said = online
    ? `Couldn't load ${what}.${hint ? ` ${hint}` : onRetry ? ' Try again in a moment.' : ''}`
    : `You're offline, so ${what} can't load. It comes back when you're connected.`
  const retry = online && onRetry
  if (compact)
    return (
      <div
        className={cx('flex-1 flex flex-col items-start justify-center gap-1.5', className)}
        role="alert"
        title={online && reason ? reason : undefined}
        data-testid="failed"
      >
        <p className="text-[13px] text-fg-2 leading-snug flex gap-1.5">
          <CircleAlert size={14} className={cx('mt-[2px] shrink-0', failedMark)} aria-hidden />
          {said}
        </p>
        {retry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full bg-raised border border-line px-3 min-h-8 text-xs"
          >
            Try again
          </button>
        )}
      </div>
    )
  return (
    <div
      className={cx('card px-4 py-3 flex items-start gap-3 fade-in', className)}
      role="alert"
      data-testid="failed"
    >
      <CircleAlert size={18} className={cx('mt-0.5 shrink-0', failedMark)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[15px] text-fg leading-snug">{said}</p>
        {online && reason && (
          <p className="mt-1 text-xs text-faint break-words" data-testid="failed-reason">
            {reason}
          </p>
        )}
        {retry && (
          <Button className="mt-2.5" onClick={onRetry} data-testid="failed-retry">
            Try again
          </Button>
        )}
      </div>
    </div>
  )
}

export const PageTitle = ({
  children,
  right,
  eyebrow,
}: {
  children: ReactNode
  right?: ReactNode
  eyebrow?: ReactNode
}) => (
  <div className="flex items-end justify-between gap-3 mb-4">
    <div>
      {eyebrow && <div className="text-[13px] font-semibold text-faint mb-0.5">{eyebrow}</div>}
      <h1 className="t-title">{children}</h1>
    </div>
    {right && <div className="shrink-0 pb-0.5">{right}</div>}
  </div>
)

/** Section label inside a page. */
export const SectionTitle = ({ children, right }: { children: ReactNode; right?: ReactNode }) => (
  <div className="flex items-center justify-between mb-2 mt-1">
    <h2 className="text-[13px] font-semibold text-muted">{children}</h2>
    {right}
  </div>
)

/**
 * Bottom sheet on mobile (with a grab handle), centred dialog on desktop.
 * A column: header, scrolling body, optional footer that stays put (Save /
 * Cancel). The body and footer keep clear of the home indicator.
 */
export const Sheet = ({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
}) => {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])
  if (!open) return null
  // into <body>: a sheet opened from inside a layer (the launcher sits under the
  // tab bar) would otherwise share that layer and open behind the bar
  return createPortal(
    <div
      className="kb-follow fixed inset-x-0 z-40 flex items-end md:items-center md:justify-center"
      style={{ top: 'var(--vv-top)', height: 'var(--vv-height)' }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onClose} />
      <div className="sheet-panel relative w-full md:max-w-lg bg-panel border border-line md:rounded-2xl rounded-t-3xl max-h-[88%] flex flex-col sheet-in shadow-[var(--shadow-pop)]">
        <div className="shrink-0 px-4 pt-3">
          <div className="md:hidden mx-auto mb-2 h-1 w-10 rounded-full bg-line-strong" />
          {title && (
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold tracking-tight">{title}</h2>
              <IconButton label="Close" onClick={onClose}>
                <CloseGlyph />
              </IconButton>
            </div>
          )}
        </div>
        <div
          className={cx(
            'min-h-0 overflow-y-auto overflow-x-hidden px-4',
            footer ? 'pb-2' : 'pb-[calc(1.5rem+env(safe-area-inset-bottom))] md:pb-4',
          )}
          data-sheet-body
        >
          {children}
        </div>
        {footer && (
          <div className="shrink-0 flex justify-end gap-2 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-3 border-t border-line">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

export const Field = ({
  label,
  children,
  hint,
  error,
}: {
  label: string
  children: ReactNode
  hint?: string
  error?: string
}) => (
  <label className="block mb-3 min-w-0">
    <span className="block text-[13px] font-semibold text-muted mb-1.5">{label}</span>
    {children}
    {hint && !error && <span className="block text-xs text-faint mt-1">{hint}</span>}
    {error && <span className="block text-xs text-rose-300 mt-1">{error}</span>}
  </label>
)

/** Segmented control for exclusive choices (Today's time/energy pickers). */
export const Segmented = <T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  label: string
}) => (
  <div
    role="radiogroup"
    aria-label={label}
    className="inline-flex rounded-full bg-raised border border-line p-0.5"
  >
    {options.map((o) => (
      <button
        key={String(o.value)}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        onClick={() => onChange(o.value)}
        className={cx(
          'rounded-full px-2.5 min-h-7 text-xs font-medium transition-colors',
          value === o.value ? 'bg-accent text-on-accent' : 'text-muted hover:text-fg',
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
)

export { cx }

/**
 * A panel that slides in from the side: a list beside the thing you are doing
 * rather than on top of it. Same safe-area and Escape handling as Sheet.
 */
export const SideSheet = ({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
}) => {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])
  if (!open) return null
  return (
    <div
      className="kb-follow fixed inset-x-0 z-40 flex justify-end"
      style={{ top: 'var(--vv-top)', height: 'var(--vv-height)' }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-[82%] max-w-sm h-full bg-panel border-l border-line flex flex-col side-in shadow-[var(--shadow-pop)]">
        <div className="shrink-0 flex items-center justify-between px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3">
          {title && <h2 className="text-base font-semibold tracking-tight">{title}</h2>}
          <IconButton label="Close" onClick={onClose}>
            <CloseGlyph />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          {children}
        </div>
      </div>
    </div>
  )
}
